import {
  CrossIcon_default
} from "./chunk-DZJXRHCV.jsx";
import {
  Conditional
} from "./chunk-AAE3RIHT.jsx";
import {
  __name
} from "./chunk-DVUTYNGK.jsx";

// src/components/common/Modal/Modal.tsx
import {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect
} from "react";
import { createPortal } from "react-dom";

// src/components/common/Modal/styles.ts
import { sva } from "@headout/pixie/css";
var modalStyles = sva({
  slots: ["root", "backdrop", "content", "closeButton", "header", "body"],
  base: {
    root: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    backdrop: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      width: "100vw",
      animation: `fadeIn 0.3s ease forwards`,
      '&[data-is-open="false"]': {
        animation: `fadeOut 0.3s ease forwards`
      }
    },
    content: {
      backgroundColor: "core.primary.white",
      borderRadius: "radius.8",
      boxShadow: "shadow.level.2",
      maxWidth: "[90%]",
      maxHeight: "[90%]",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      transform: "translateY(20px) scale(0.95)",
      position: "relative",
      animation: `fadeIn 0.3s ease forwards, scaleIn 0.3s ease forwards`,
      '&[data-is-open="false"]': {
        animation: `fadeOut 0.3s ease forwards, scaleOut 0.3s ease forwards`
      }
    },
    closeButton: {
      position: "absolute",
      display: "flex",
      right: "space.6",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      border: "none",
      background: "transparent",
      padding: 0,
      color: "core.grey.100",
      transition: "color 0.2s ease",
      zIndex: 2,
      _hover: {
        color: "core.primary.black",
        backgroundColor: "transparent"
      },
      _focus: {
        outline: "none"
      }
    },
    header: {
      paddingX: "space.24",
      paddingY: "space.12",
      borderBottom: "1px solid token(colors.core.grey.200)",
      fontWeight: "bold",
      position: "sticky",
      top: 0,
      backgroundColor: "core.primary.white",
      zIndex: 1,
      transition: "transform 0.3s ease",
      '&[data-hidden="true"]': {
        transform: "translateY(-100%)"
      }
    },
    body: {
      padding: "space.24",
      overflow: "auto",
      flex: 1
    }
  }
});

// src/components/common/Modal/Modal.tsx
import { Button } from "@headout/eevee";
import { cx } from "@headout/pixie/css";
var Modal = forwardRef(
  ({
    children,
    onClose,
    onOpen,
    closeOnBackdropClick = true,
    closeOnEscape = true,
    initialOpen = false,
    showCloseIcon = false,
    closeIcon,
    header,
    headerScrollBehavior = true,
    className,
    backdropClassName,
    contentClassName,
    headerClassName,
    bodyClassName,
    key,
    /**
     * This is because z-index of Dweb header is 100.
     */
    zIndex = 101
  }, ref) => {
    const [isOpen, setIsOpen] = useState(initialOpen);
    const [animationState, setAnimationState] = useState(
      initialOpen ? "open" : "closed"
    );
    const [isMounted, setIsMounted] = useState(false);
    const [isHeaderHidden, setIsHeaderHidden] = useState(false);
    const contentRef = useRef(null);
    const bodyRef = useRef(null);
    const lastScrollTop = useRef(0);
    const previousFocusRef = useRef(null);
    const closeTimeoutRef = useRef();
    const styles = modalStyles();
    useEffect(() => {
      setIsMounted(true);
      return () => {
        setIsMounted(false);
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
      };
    }, []);
    useEffect(() => {
      const handleEscape = /* @__PURE__ */ __name((event) => {
        if (closeOnEscape && event.key === "Escape" && isOpen) {
          close();
        }
      }, "handleEscape");
      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        document.body.style.overflow = "hidden";
      }
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    }, [isOpen, closeOnEscape]);
    useEffect(() => {
      if (!headerScrollBehavior || !header || !bodyRef.current) return;
      const handleScroll = /* @__PURE__ */ __name(() => {
        var _a;
        const scrollTop = ((_a = bodyRef.current) == null ? void 0 : _a.scrollTop) || 0;
        const isScrollingDown = scrollTop > lastScrollTop.current;
        setIsHeaderHidden(isScrollingDown);
        lastScrollTop.current = scrollTop;
      }, "handleScroll");
      const bodyElement = bodyRef.current;
      bodyElement.addEventListener("scroll", handleScroll);
      return () => {
        bodyElement.removeEventListener("scroll", handleScroll);
      };
    }, [headerScrollBehavior, header]);
    const open = /* @__PURE__ */ __name(() => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      previousFocusRef.current = document.activeElement;
      setIsOpen(true);
      setAnimationState("open");
      onOpen == null ? void 0 : onOpen();
      setTimeout(() => {
        const modalRoot = document.querySelector(
          '[data-testid="modal-root"]'
        );
        if (modalRoot instanceof HTMLElement) {
          modalRoot.focus();
        }
      }, 0);
    }, "open");
    const close = /* @__PURE__ */ __name(() => {
      setAnimationState("closed");
      closeTimeoutRef.current = setTimeout(() => {
        setIsOpen(false);
        onClose == null ? void 0 : onClose();
        if (previousFocusRef.current) {
          previousFocusRef.current.focus();
        }
      }, 300);
    }, "close");
    useImperativeHandle(ref, () => ({
      open,
      close,
      isOpen
    }));
    const handleBackdropClick = /* @__PURE__ */ __name((e) => {
      if (closeOnBackdropClick && e.target === e.currentTarget) {
        close();
      }
    }, "handleBackdropClick");
    const handleBackdropKeyDown = /* @__PURE__ */ __name((e) => {
      if (e.key === "Enter" || e.key === " ") {
        if (closeOnBackdropClick) {
          close();
        }
      }
    }, "handleBackdropKeyDown");
    const handleContentKeyDown = /* @__PURE__ */ __name((e) => {
      e.stopPropagation();
    }, "handleContentKeyDown");
    if (!isMounted) return null;
    return createPortal(
      isOpen ? <div
        className={cx(styles.root, className)}
        role='dialog'
        aria-modal='true'
        data-testid='modal-root'
        style={{
          zIndex
        }}
        tabIndex={-1}
      >
					<div
        className={cx(styles.backdrop, backdropClassName)}
        onClick={handleBackdropClick}
        onKeyDown={handleBackdropKeyDown}
        data-is-open={animationState === "open"}
        data-qa-marker='qaid-modal-backdrop'
        data-testid='modal-backdrop'
        tabIndex={-1}
        role='presentation'
      />
					<div
        className={cx(styles.content, contentClassName)}
        ref={contentRef}
        data-is-open={animationState === "open"}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleContentKeyDown}
        data-qa-marker='qaid-modal-content'
        data-testid='modal-content-container'
        role='presentation'
      >
						<Conditional if={!!header}>
							<div
        className={cx(styles.header, headerClassName)}
        data-hidden={isHeaderHidden}
        data-testid='modal-header'
      >
								{header}
							</div>
						</Conditional>

						<Conditional if={showCloseIcon}>
							<Button
        as='button'
        btnType='transparent'
        icon={closeIcon || <CrossIcon_default
          height={16}
          width={16}
          data-testid='modal-close-icon'
        />}
        iconPosition='leading'
        primaryText=''
        size='medium'
        state='default'
        variant='primary'
        className={styles.closeButton}
        onClick={close}
        aria-label='Close modal'
        data-testid='modal-close-button'
      />
						</Conditional>

						<div
        className={cx(styles.body, bodyClassName)}
        ref={bodyRef}
        data-testid='modal-body'
      >
							{children}
						</div>
					</div>
				</div> : null,
      document.body,
      key
    );
  }
);
Modal.displayName = "Modal";
var Modal_default = Modal;

export {
  Modal_default
};
